/**
 * Günün Özeti Kartı — BOSS ana sayfası.
 * İki sekme: Bugün (canlı) / Dün (özet). Her satır ilgili ekrana tıklanabilir.
 *
 * İçerik: vardiya sayısı, şu an mesaide (bugün), bekleyen talepler (avans/izin/extra),
 * görev durumu (tamam/sorunlu/bekliyor), doğum günleri.
 *
 * Kendi verisini çeker (BossOverview'ı şişirmez). Veriler canlı Firestore'dan.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CalendarDays, Activity, Wallet, CalendarOff, ClipboardList,
  Cake, ChevronRight, Loader2, Sun,
} from 'lucide-react'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { sonKayitHaritasi, type Kayit } from '@/lib/services/kayit-service'
import {
  tumGorevler, tumTamamlamalar, gorevGununDurumu,
} from '@/lib/services/gorev-service'
import type { Vardiya } from '@/lib/services/vardiya-service'
import type { KullaniciPersonel } from '@/types'

type Sekme = 'bugun' | 'dun'

interface OzetVeri {
  vardiyaSayisi: number
  vardiyaKisiSayisi: number
  mesaideSayisi: number | null   // sadece bugün için
  bekleyenAvans: number
  bekleyenIzin: number
  bekleyenExtra: number
  devamsizSayisi: number
  gorevTamam: number
  gorevSorunlu: number
  gorevBekliyor: number
  dogumGunleri: string[]
}

function isoTarih(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function GununOzetiKarti() {
  const navigate = useNavigate()
  const [sekme, setSekme] = useState<Sekme>('bugun')
  const [yukleniyor, setYukleniyor] = useState(true)
  const [bugunVeri, setBugunVeri] = useState<OzetVeri | null>(null)
  const [dunVeri, setDunVeri] = useState<OzetVeri | null>(null)

  useEffect(() => {
    let iptal = false
    async function hesapla() {
      setYukleniyor(true)
      const bugunDt = new Date()
      const dunDt = new Date()
      dunDt.setDate(dunDt.getDate() - 1)
      const bugun = isoTarih(bugunDt)
      const dun = isoTarih(dunDt)

      const db = getFirestoreDb()
      try {
        const [
          personeller, gorevler, tamamlamalar,
          bugunVarSnap, dunVarSnap,
          avansSnap, izinSnap, extraSnap,
          bugunKayitSnap,
        ] = await Promise.all([
          tumPersonelleriGetir(),
          tumGorevler(),
          tumTamamlamalar(),
          getDocs(query(collection(db, 'vardiyalar'), where('tarih', '==', bugun))),
          getDocs(query(collection(db, 'vardiyalar'), where('tarih', '==', dun))),
          getDocs(query(collection(db, 'avanslar'), where('durum', '==', 'bekliyor'))),
          getDocs(query(collection(db, 'izinTalepleri'), where('durum', '==', 'bekliyor'))),
          getDocs(query(collection(db, 'extraMesaiTalepleri'), where('durum', '==', 'bekliyor'))),
          getDocs(query(collection(db, 'kayitlar'), where('tarih', '==', bugun))),
        ])

        if (iptal) return

        const aktifPersonel = personeller.filter((p) => p.aktif) as KullaniciPersonel[]
        const bugunVardiyalar = bugunVarSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as Vardiya)
        const dunVardiyalar = dunVarSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as Vardiya)

        // Bugünkü kayıtlar — "tarih" alanı yoksa zaman bazlı çek (fallback)
        let bugunKayitlar: Kayit[] = bugunKayitSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
        if (bugunKayitlar.length === 0) {
          // kayitlar'da 'tarih' alanı yok → bugünün başlangıcından çek
          const bas = new Date(); bas.setHours(0, 0, 0, 0)
          const ks = await getDocs(query(collection(db, 'kayitlar'), where('zaman', '>=', bas as never)))
          if (!iptal) bugunKayitlar = ks.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
        }

        const avanslar = avansSnap.docs.map((d) => d.data())
        const izinler = izinSnap.docs.map((d) => d.data())
        const extralar = extraSnap.docs.map((d) => d.data())

        function gunOzeti(tarih: string, vardiyalar: Vardiya[], mesaide: number | null): OzetVeri {
          const yayinli = vardiyalar.filter((v) => v.yayinlandi && v.durum !== 'iptal')
          const devamsiz = vardiyalar.filter((v) => v.durum === 'devamsiz').length

          // Görev durumları
          let tamam = 0, sorunlu = 0, bekliyor = 0
          const simdi = new Date()
          for (const g of gorevler) {
            if (g.durum !== 'aktif') continue
            const durumlar = gorevGununDurumu(g, tarih, aktifPersonel, vardiyalar, tamamlamalar, simdi)
            for (const kd of durumlar) {
              if (kd.durum === 'tamamlandi') tamam++
              else if (kd.durum === 'yapilamadi' || kd.durum === 'kacirildi' || kd.durum === 'reddedildi') sorunlu++
              else bekliyor++ // bekliyor + beklemede
            }
          }

          const ayGun = tarih.slice(5, 10)
          const dogumlar = aktifPersonel
            .filter((p) => typeof p.dogumTarihi === 'string' && p.dogumTarihi.slice(5, 10) === ayGun)
            .map((p) => p.isim)

          return {
            vardiyaSayisi: yayinli.length,
            vardiyaKisiSayisi: new Set(yayinli.map((v) => v.personelId)).size,
            mesaideSayisi: mesaide,
            bekleyenAvans: avanslar.length,
            bekleyenIzin: izinler.length,
            bekleyenExtra: extralar.length,
            devamsizSayisi: devamsiz,
            gorevTamam: tamam,
            gorevSorunlu: sorunlu,
            gorevBekliyor: bekliyor,
            dogumGunleri: dogumlar,
          }
        }

        // Bugün mesaide sayısı
        const sonKayitlar = sonKayitHaritasi(bugunKayitlar)
        let mesaide = 0
        aktifPersonel.forEach((p) => {
          if (sonKayitlar.get(p.id)?.tip === 'giris') mesaide++
        })

        setBugunVeri(gunOzeti(bugun, bugunVardiyalar, mesaide))
        setDunVeri(gunOzeti(dun, dunVardiyalar, null))
      } catch (e) {
        console.error('[GununOzeti] hesaplama hatası:', e)
      } finally {
        if (!iptal) setYukleniyor(false)
      }
    }
    hesapla()
    return () => { iptal = true }
  }, [])

  const veri = sekme === 'bugun' ? bugunVeri : dunVeri

  const bekleyenToplam = useMemo(() => {
    if (!veri) return 0
    return veri.bekleyenAvans + veri.bekleyenIzin + veri.bekleyenExtra
  }, [veri])

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Sun className="w-5 h-5 text-amber-500" />
          Günün Özeti
        </h2>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setSekme('bugun')}
            className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${
              sekme === 'bugun' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500'
            }`}
          >
            Bugün
          </button>
          <button
            onClick={() => setSekme('dun')}
            className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${
              sekme === 'dun' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500'
            }`}
          >
            Dün
          </button>
        </div>
      </div>

      {yukleniyor || !veri ? (
        <div className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-brand-600 inline-block" />
        </div>
      ) : (
        <div className="space-y-1.5">
          <OzetSatir
            ikon={<CalendarDays className="w-4 h-4 text-indigo-600" />}
            etiket="Vardiya"
            deger={`${veri.vardiyaSayisi} vardiya · ${veri.vardiyaKisiSayisi} kişi`}
            onClick={() => navigate('/boss/vardiyalar')}
          />
          {sekme === 'bugun' && veri.mesaideSayisi !== null && (
            <OzetSatir
              ikon={<Activity className="w-4 h-4 text-green-600" />}
              etiket="Şu an mesaide"
              deger={`${veri.mesaideSayisi} kişi`}
              onClick={() => navigate('/boss/giris-cikis')}
            />
          )}
          {veri.bekleyenAvans > 0 && (
            <OzetSatir
              ikon={<Wallet className="w-4 h-4 text-orange-600" />}
              etiket="Bekleyen avans"
              deger={`${veri.bekleyenAvans} talep`}
              onClick={() => navigate('/boss/avanslar')}
              vurgu
            />
          )}
          {veri.bekleyenIzin > 0 && (
            <OzetSatir
              ikon={<CalendarDays className="w-4 h-4 text-orange-600" />}
              etiket="Bekleyen izin"
              deger={`${veri.bekleyenIzin} talep`}
              onClick={() => navigate('/boss/izinler')}
              vurgu
            />
          )}
          {veri.bekleyenExtra > 0 && (
            <OzetSatir
              ikon={<Activity className="w-4 h-4 text-orange-600" />}
              etiket="Bekleyen extra mesai"
              deger={`${veri.bekleyenExtra} talep`}
              onClick={() => navigate('/boss/giris-cikis')}
              vurgu
            />
          )}
          {bekleyenToplam === 0 && (
            <OzetSatir
              ikon={<Wallet className="w-4 h-4 text-gray-400" />}
              etiket="Bekleyen talep"
              deger="Yok"
            />
          )}
          {veri.devamsizSayisi > 0 && (
            <OzetSatir
              ikon={<CalendarOff className="w-4 h-4 text-red-600" />}
              etiket="Devamsızlık"
              deger={`${veri.devamsizSayisi} vardiya`}
              onClick={() => navigate('/boss/giris-cikis')}
              vurgu
            />
          )}
          <OzetSatir
            ikon={<ClipboardList className="w-4 h-4 text-blue-600" />}
            etiket="Görevler"
            deger={
              (veri.gorevTamam + veri.gorevSorunlu + veri.gorevBekliyor) === 0
                ? 'Görev yok'
                : `✅ ${veri.gorevTamam} · ⚠️ ${veri.gorevSorunlu} · ⏳ ${veri.gorevBekliyor}`
            }
            onClick={() => navigate('/boss/gorevler')}
          />
          {veri.dogumGunleri.length > 0 && (
            <OzetSatir
              ikon={<Cake className="w-4 h-4 text-pink-600" />}
              etiket="Doğum günü"
              deger={veri.dogumGunleri.join(', ')}
              onClick={() => navigate('/boss/dogumgunleri')}
              vurgu
            />
          )}
        </div>
      )}
    </div>
  )
}

interface SatirProps {
  ikon: React.ReactNode
  etiket: string
  deger: string
  onClick?: () => void
  vurgu?: boolean
}

function OzetSatir({ ikon, etiket, deger, onClick, vurgu }: SatirProps) {
  const tiklanabilir = !!onClick
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
        vurgu ? 'bg-amber-50' : 'bg-gray-50'
      } ${tiklanabilir ? 'cursor-pointer hover:bg-gray-100' : ''}`}
    >
      <div className="flex-shrink-0">{ikon}</div>
      <div className="flex-1 min-w-0">
        <span className="text-xs text-gray-500">{etiket}</span>
        <p className="text-sm font-semibold text-gray-900 truncate">{deger}</p>
      </div>
      {tiklanabilir && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
    </div>
  )
}
